import { useEffect, useState, useMemo } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { inr, inrFull } from "../lib/format";
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Users,
  CurrencyInr,
  Buildings,
  Crown,
  ArrowCircleUp,
  ArrowCircleDown,
  Gift,
  Prohibit,
  Clock,
  ChatCircleDots,
  Bug,
  Lightbulb,
  Heart,
  UserPlus,
} from "@phosphor-icons/react";
import WalkthroughsPanel from "../components/admin/WalkthroughsPanel";
import LandingVideosPanel from "../components/admin/LandingVideosPanel";
import ProductUpdatesPanel from "../components/admin/ProductUpdatesPanel";
import PricingPanel from "../components/admin/PricingPanel";
import ActivityPanel from "../components/admin/ActivityPanel";
import OpportunityPanel from "../components/admin/OpportunityPanel";
import RazorpayWebhooksPanel from "../components/admin/RazorpayWebhooksPanel";
import NodesPanel from "../components/admin/NodesPanel";
import SocialPanel from "../components/admin/SocialPanel";
import ChatbotControlPanel from "../components/admin/ChatbotControlPanel";
import NetWorthV2AdminPanel from "../components/admin/NetWorthV2AdminPanel";
import SecretsPanel from "../components/admin/SecretsPanel";
import SEOPanel from "../components/admin/SEOPanel";
import FeatureTogglesPanel from "../components/admin/FeatureTogglesPanel";

export default function Admin() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabRaw] = useState(searchParams.get("tab") || "overview");
  const setTab = (newTab) => {
    setTabRaw(newTab);
    setSearchParams(newTab === "overview" ? {} : { tab: newTab }, { replace: true });
  };
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [txns, setTxns] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuper = user?.role === "admin";
  const isNodeAdmin = user?.role === "node_admin";

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, n, t, f, lw] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/nodes").catch(() => ({ data: [] })),
        api.get("/admin/transactions").catch(() => ({ data: [] })),
        api.get("/admin/feedback").catch(() => ({ data: [] })),
        api.get("/admin/lawyers").catch(() => ({ data: { lawyers: [] } })),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setNodes(n.data);
      setTxns(t.data);
      setFeedback(f.data);
      setLawyers(lw.data.lawyers || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "node_admin") load();
  }, [user]);

  const tabsList = useMemo(() => [
    ["overview", "Overview"],
    ["users", `Users (${users.length})`],
    isSuper && ["nodes", `Nodes (${nodes.length})`],
    isSuper && ["social", "Social posts"],
    isSuper && ["pricing", "Pricing"],
    isSuper && ["activity", "Live activity"],
    isSuper && ["opportunity", "Add-on insights"],
    isSuper && ["razorpay", "Razorpay webhooks"],
    isSuper && ["lawyers", `Lawyers (${lawyers.length})`],
    isSuper && ["transactions", `Transactions (${txns.length})`],
    isSuper && ["feedback", `Feedback (${feedback.length})`],
    isSuper && ["analytics", "Analytics"],
    isSuper && ["portfolio-analytics", "Portfolio analytics"],
    isSuper && ["walkthroughs", "Walkthroughs"],
    isSuper && ["landing-videos", "Landing videos"],
    isSuper && ["product-updates", "What's new"],
    isSuper && ["data-viewer", "Data viewer"],
    isSuper && ["email-test", "Email test"],
    isSuper && ["chatbot", "Estima bot"],
    isSuper && ["nwv2", "Net worth & history"],
    isSuper && ["secrets", "API keys"],
    isSuper && ["seo", "SEO cron"],
    isSuper && ["features", "Feature toggles"],
  ].filter(Boolean), [users.length, nodes.length, lawyers.length, txns.length, feedback.length, isSuper]);

  if (user && user.role !== "admin" && user.role !== "node_admin") {
    return <Navigate to="/app" replace />;
  }

  const assignNode = async (userId, currentNodeId) => {
    const choices = nodes.map((n, i) => `${i + 1}. ${n.name}`).join("\n");
    const prompt_text = `Assign user to which node?\n0. (Unassign)\n${choices}\n\nEnter number:`;
    const pick = prompt(prompt_text, currentNodeId ? "0" : "1");
    if (pick === null) return;
    const idx = parseInt(pick, 10);
    if (isNaN(idx)) return;
    try {
      const target_node_id = idx === 0 ? null : nodes[idx - 1]?.id;
      if (idx !== 0 && !target_node_id) { toast.error("Invalid choice"); return; }
      await api.post(`/admin/users/${userId}/assign-node`, { node_id: target_node_id });
      toast.success(idx === 0 ? "Unassigned." : `Assigned to ${nodes[idx - 1].name}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const act = async (id, action, days = 30) => {
    try {
      await api.post(`/admin/users/${id}/action`, { action, days });
      toast.success("Done");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const legacyTransfer = async (srcUser) => {
    const new_email = prompt(`Transfer ${srcUser.email}'s portfolio to next of kin. Enter new email:`);
    if (!new_email) return;
    const new_name = prompt("New user's full name:");
    if (!new_name) return;
    const note = prompt("Note (optional, e.g. 'Inherited from father'):", "") || "";
    try {
      const { data } = await api.post("/admin/legacy-transfer", {
        from_user_id: srcUser.id,
        new_email: new_email.trim().toLowerCase(),
        new_name: new_name.trim(),
        transfer_properties: true,
        transfer_tenants: true,
        note,
      });
      toast.success(
        `Transferred: ${data.transferred.properties} properties, ${data.transferred.tenants} tenants · temp password: ${data.temp_password}`,
        { duration: 15000 }
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10" data-testid="admin-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif text-3xl sm:text-5xl">
            The control room
            <span className="text-[hsl(var(--primary))]">.</span>
          </h1>
          {isNodeAdmin && (
            <p className="text-xs text-muted-foreground mt-2" data-testid="admin-scope-banner">
              You're a <span className="text-[hsl(var(--secondary))]">nodal admin</span> — your view is scoped to users assigned to your node.
            </p>
          )}
        </div>
        <span className="eyebrow text-[hsl(var(--primary))] inline-flex items-center gap-1" data-testid="admin-role-badge">
          <Crown size={14} weight="duotone" /> {isSuper ? "super admin" : "node admin"}
        </span>
      </div>

      <div className="border-b hairline mb-8 flex gap-1 flex-wrap">
        {tabsList.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`admin-tab-${id}`}
            className={`px-5 py-2.5 text-sm -mb-px border-b-2 transition ${
              tab === id
                ? "border-[hsl(var(--primary))] text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground" data-testid="admin-loading">
          Loading…
        </div>
      ) : tab === "overview" ? (
        <Overview stats={stats} />
      ) : tab === "users" ? (
        <UsersTable users={users} nodes={nodes} currentUserId={user?.id} isSuper={isSuper} onAct={act} onLegacyTransfer={legacyTransfer} onAssignNode={assignNode} />
      ) : tab === "nodes" ? (
        <NodesPanel users={users} onRefreshUsers={load} />
      ) : tab === "social" ? (
        <SocialPanel />
      ) : tab === "pricing" ? (
        <PricingPanel />
      ) : tab === "activity" ? (
        <ActivityPanel />
      ) : tab === "opportunity" ? (
        <OpportunityPanel />
      ) : tab === "razorpay" ? (
        <RazorpayWebhooksPanel />
      ) : tab === "lawyers" ? (
        <LawyersTable lawyers={lawyers} onRefresh={load} />
      ) : tab === "feedback" ? (
        <FeedbackTable feedback={feedback} onRefresh={load} />
      ) : tab === "analytics" ? (
        <AnalyticsPanel />
      ) : tab === "portfolio-analytics" ? (
        <PortfolioAnalyticsPanel />
      ) : tab === "walkthroughs" ? (
        <WalkthroughsPanel />
      ) : tab === "landing-videos" ? (
        <LandingVideosPanel />
      ) : tab === "product-updates" ? (
        <ProductUpdatesPanel />
      ) : tab === "data-viewer" ? (
        <DataViewerPanel />
      ) : tab === "email-test" ? (
        <EmailTestPanel />
      ) : tab === "chatbot" ? (
        <ChatbotControlPanel />
      ) : tab === "nwv2" ? (
        <NetWorthV2AdminPanel />
      ) : tab === "secrets" ? (
        <SecretsPanel />
      ) : tab === "seo" ? (
        <SEOPanel />
      ) : tab === "features" ? (
        <FeatureTogglesPanel />
      ) : (
        <TxnsTable txns={txns} />
      )}
    </div>
  );
}

function Overview({ stats }) {
  if (!stats) return null;
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <StatCard
        label="Users"
        big={stats.users.total}
        rows={[
          ["Pro", stats.users.pro, "secondary"],
          ["Trial", stats.users.trial],
          ["Free", stats.users.free],
        ]}
        icon={<Users size={22} weight="duotone" />}
        testid="stat-users"
      />
      <StatCard
        label="Revenue"
        big={inrFull(stats.revenue.total_inr)}
        rows={[
          ["Successful payments", stats.revenue.successful_transactions],
          ["Average ticket", stats.revenue.successful_transactions ? inr(stats.revenue.total_inr / stats.revenue.successful_transactions) : "—"],
        ]}
        icon={<CurrencyInr size={22} weight="duotone" />}
        testid="stat-revenue"
      />
      <StatCard
        label="Properties"
        big={stats.properties.total}
        rows={[
          ["Owned", stats.properties.owned],
          ["Sold", stats.properties.sold],
          ["Evaluating", Math.max(stats.properties.total - stats.properties.owned - stats.properties.sold, 0)],
        ]}
        icon={<Buildings size={22} weight="duotone" />}
        testid="stat-properties"
      />
    </div>
  );
}

function StatCard({ label, big, rows, icon, testid }) {
  return (
    <div className="card-flat p-6" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className="eyebrow">{label}</div>
        <div className="text-[hsl(var(--secondary))]">{icon}</div>
      </div>
      <div className="num-metric text-4xl mb-4">{big}</div>
      <div className="border-t hairline pt-4 space-y-2 text-sm">
        {rows.map(([k, v, tone]) => (
          <div key={k} className="flex justify-between">
            <span className="text-muted-foreground">{k}</span>
            <span className={tone === "secondary" ? "text-[hsl(var(--secondary))]" : "text-foreground"}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTable({ users, nodes = [], currentUserId, isSuper = true, onAct, onLegacyTransfer, onAssignNode }) {
  return (
    <div className="card-flat overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground border-b hairline">
          <tr>
            {["User", "Role", "Plan", "Status", "Add-ons", "Assigned Node", "Device", "City", "Net worth", "Signup IP", "Last login", "Actions"].map((h) => (
              <th key={h} className="p-4 font-normal eyebrow">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b hairline last:border-0" data-testid={`admin-user-row-${u.id}`}>
              <td className="p-4">
                <div className="font-serif text-lg">{u.name || u.email.split("@")[0]}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </td>
              <td className="p-4">
                <span className={`text-xs uppercase tracking-widest ${u.role === "admin" ? "text-[hsl(var(--primary))]" : u.role === "node_admin" ? "text-[hsl(var(--secondary))]" : "text-muted-foreground"}`}>
                  {u.role}
                </span>
              </td>
              <td className="p-4">
                <span className={u.is_pro ? "text-[hsl(var(--secondary))]" : "text-muted-foreground"}>
                  {u.plan}
                </span>
                <div className="text-[10px] text-muted-foreground">{u.plan_status}{u.plan_cycle ? ` · ${u.plan_cycle}` : ""}</div>
              </td>
              <td className="p-4 text-xs text-muted-foreground">
                {u.plan_status === "trial"
                  ? u.trial_ends_at
                    ? new Date(u.trial_ends_at).toLocaleDateString()
                    : "—"
                  : u.plan_expires_at
                    ? new Date(u.plan_expires_at).toLocaleDateString()
                    : "—"}
              </td>
              <td className="p-4 text-xs" data-testid={`admin-user-addons-${u.id}`}>
                {Array.isArray(u.addons) && u.addons.length > 0
                  ? <div className="flex flex-wrap gap-1">{u.addons.slice(0, 6).map((a) => (
                      <span key={a} className="bg-[hsl(var(--muted))] px-1.5 py-0.5 text-[10px] rounded-sm">{a}</span>
                    ))}{u.addons.length > 6 && <span className="text-[10px] text-muted-foreground">+{u.addons.length - 6}</span>}</div>
                  : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="p-4 text-xs" data-testid={`admin-user-node-${u.id}`}>
                {u.node_name ? (
                  <span className="bg-[hsl(var(--muted))] px-1.5 py-0.5 text-[10px] rounded-sm">{u.node_name}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
                {isSuper && (
                  <button
                    onClick={() => onAssignNode?.(u.id, u.node_id)}
                    className="block text-[10px] text-muted-foreground hover:text-foreground mt-1 underline"
                    data-testid={`admin-assign-node-${u.id}`}
                  >
                    {u.node_name ? "change" : "assign"}
                  </button>
                )}
              </td>
              <td className="p-4 text-xs" data-testid={`admin-user-device-${u.id}`}>
                <span className={`inline-flex items-center gap-1 ${u.device === "mobile" ? "text-[hsl(var(--secondary))]" : "text-muted-foreground"}`}>
                  {u.device === "mobile" ? "📱 Mobile" : u.device === "laptop" ? "💻 Laptop" : "—"}
                </span>
              </td>
              <td className="p-4 text-xs" data-testid={`admin-user-city-${u.id}`}>{u.city || "—"}</td>
              <td className="p-4 text-xs" data-testid={`admin-user-networth-${u.id}`}>
                <div>{u.net_worth_bucket || "—"}</div>
                {u.net_worth ? (
                  <div className="text-[10px] text-muted-foreground">
                    ₹{Number(u.net_worth).toLocaleString("en-IN")}
                  </div>
                ) : null}
              </td>
              <td className="p-4 text-xs font-mono" data-testid={`admin-user-signup-ip-${u.id}`}>{u.signup_ip || "—"}</td>
              <td className="p-4 text-xs">
                <div className="font-mono">{u.last_login_ip || "—"}</div>
                <div className="text-[10px] text-muted-foreground">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "—"}
                </div>
              </td>
              <td className="p-4">
                <div className="flex gap-1.5">
                  {isSuper && (u.role !== "admin" ? (
                    <button onClick={() => onAct(u.id, "promote")} className="btn-ghost p-1.5 text-xs" title="Promote to admin" data-testid={`admin-promote-${u.id}`}>
                      <ArrowCircleUp size={14} />
                    </button>
                  ) : u.id !== currentUserId ? (
                    <button onClick={() => onAct(u.id, "demote")} className="btn-ghost p-1.5 text-xs" title="Demote" data-testid={`admin-demote-${u.id}`}>
                      <ArrowCircleDown size={14} />
                    </button>
                  ) : null)}
                  {!u.is_pro ? (
                    <button onClick={() => onAct(u.id, "grant_pro", 30)} className="btn-ghost p-1.5 text-xs" title="Grant 30 days Pro" data-testid={`admin-grant-${u.id}`}>
                      <Gift size={14} />
                    </button>
                  ) : (
                    <button onClick={() => onAct(u.id, "cancel_pro")} className="btn-ghost p-1.5 text-xs" title="Cancel Pro" data-testid={`admin-cancel-${u.id}`}>
                      <Prohibit size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      const days = parseInt(prompt("Extend trial by how many days?", "30") || "0", 10);
                      if (days > 0) onAct(u.id, "extend_trial", days);
                    }}
                    className="btn-ghost p-1.5 text-xs"
                    title="Extend trial"
                    data-testid={`admin-extend-trial-${u.id}`}
                  >
                    <Clock size={14} />
                  </button>
                  {isSuper && (
                    <button
                      onClick={() => onLegacyTransfer(u)}
                      className="btn-ghost p-1.5 text-xs"
                      title="Transfer portfolio to next-of-kin (create legacy login)"
                      data-testid={`admin-legacy-${u.id}`}
                    >
                      <UserPlus size={14} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TxnsTable({ txns }) {
  if (txns.length === 0)
    return <div className="card-flat p-12 text-center text-muted-foreground" data-testid="admin-txns-empty">No transactions yet.</div>;
  return (
    <div className="card-flat overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground border-b hairline">
          <tr>
            {["When", "User", "Plan", "Amount", "Status", "Order / Payment"].map((h) => (
              <th key={h} className="p-4 font-normal eyebrow">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {txns.map((t, i) => (
            <tr key={t.order_id || i} className="border-b hairline last:border-0" data-testid={`admin-txn-${t.order_id}`}>
              <td className="p-4 text-xs text-muted-foreground">
                {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
              </td>
              <td className="p-4">{t.email}</td>
              <td className="p-4">{t.plan_id}</td>
              <td className="p-4 num-metric">{inr(t.amount_inr)}</td>
              <td className="p-4">
                <span
                  className={`text-xs uppercase tracking-widest ${
                    t.status === "success"
                      ? "text-[hsl(var(--secondary))]"
                      : t.status === "failed"
                        ? "text-destructive"
                        : "text-muted-foreground"
                  }`}
                >
                  {t.status}
                </span>
              </td>
              <td className="p-4 font-mono text-[0.7rem] text-muted-foreground">
                <div>{t.order_id}</div>
                {t.payment_id && <div>{t.payment_id}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


const CAT_ICON = { general: ChatCircleDots, bug: Bug, idea: Lightbulb, love: Heart };
const CAT_COLOR = {
  general: "text-muted-foreground",
  bug: "text-destructive",
  idea: "text-[hsl(var(--secondary))]",
  love: "text-[hsl(var(--primary))]",
};

function FeedbackTable({ feedback, onRefresh }) {
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const setStatus = async (fid, status) => {
    try {
      await api.post(`/admin/feedback/${fid}/status`, { status });
      toast.success("Updated");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !replyFor) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/feedback/${replyFor.id}/reply`, { reply_text: replyText.trim() });
      toast.success(data.emailed ? "Reply emailed" : "Reply saved (no email on file)");
      setReplyFor(null); setReplyText("");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (feedback.length === 0)
    return (
      <div className="card-flat p-12 text-center text-muted-foreground" data-testid="admin-feedback-empty">
        No feedback yet.
      </div>
    );
  return (
    <div className="space-y-3" data-testid="admin-feedback-list">
      {feedback.map((f) => {
        const Icon = CAT_ICON[f.category] || ChatCircleDots;
        return (
          <div key={f.id} className="card-flat p-5" data-testid={`admin-feedback-${f.id}`}>
            <div className="flex items-start gap-4">
              <div className={`h-9 w-9 border hairline flex items-center justify-center shrink-0 ${CAT_COLOR[f.category] || ""}`}>
                <Icon size={16} weight="duotone" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <div>
                    <span className="eyebrow capitalize">{f.category}</span>
                    {f.rating && <span className="ml-3 text-xs text-muted-foreground">rating: {f.rating}/5</span>}
                    {f.page && <span className="ml-3 text-xs text-muted-foreground font-mono">{f.page}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setReplyFor(f); setReplyText(""); }}
                      className="text-xs underline text-[hsl(var(--secondary))]"
                      data-testid={`admin-feedback-reply-${f.id}`}
                    >
                      Reply
                    </button>
                    <span className={`text-xs uppercase tracking-widest ${f.status === "new" ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}>
                      {f.status}
                    </span>
                    <select
                      value={f.status}
                      onChange={(e) => setStatus(f.id, e.target.value)}
                      className="input-dark text-xs px-2 py-1"
                      data-testid={`admin-feedback-status-${f.id}`}
                    >
                      {["new", "triaged", "planned", "done", "wontfix", "replied"].map((s) => (<option key={s}>{s}</option>))}
                    </select>
                  </div>
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">{f.message}</div>
                <div className="mt-3 text-xs text-muted-foreground">
                  from <span className="text-foreground">{f.email || "(anonymous)"}</span> · {new Date(f.created_at).toLocaleString()}
                </div>
                {f.replies?.length > 0 && (
                  <div className="mt-4 space-y-2 border-l-2 border-[hsl(var(--secondary))] pl-3" data-testid={`admin-feedback-replies-${f.id}`}>
                    {f.replies.map((r, idx) => (
                      <div key={`${r.at}-${idx}`} className="text-xs">
                        <span className="text-[hsl(var(--secondary))]">{r.by}</span>{" "}
                        <span className="text-muted-foreground">· {new Date(r.at).toLocaleString()}</span>
                        <div className="text-sm whitespace-pre-wrap mt-1">{r.text}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {replyFor && (
        <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4" data-testid="admin-feedback-reply-modal">
          <button className="absolute inset-0 bg-black/70" onClick={() => setReplyFor(null)} aria-label="Close" />
          <div className="relative card-flat w-full max-w-lg p-6">
            <div className="eyebrow mb-2">Reply to {replyFor.email || "user"}</div>
            <blockquote className="text-xs text-muted-foreground border-l-2 border-[hsl(var(--secondary))] pl-3 mb-4 line-clamp-3">{replyFor.message}</blockquote>
            <textarea
              autoFocus
              rows={6}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Hi, thanks for writing in…"
              className="input-dark w-full px-3 py-2 text-sm"
              data-testid="admin-feedback-reply-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setReplyFor(null)} className="btn-ghost px-4 py-2 text-sm" data-testid="admin-feedback-reply-cancel">Cancel</button>
              <button
                onClick={sendReply}
                disabled={sending || !replyText.trim()}
                className="btn-primary px-4 py-2 text-sm"
                data-testid="admin-feedback-reply-send"
              >
                {sending ? "Sending…" : (replyFor.email ? "Send & email" : "Save reply")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function LawyersTable({ lawyers, onRefresh }) {
  const verify = async (id, verified) => {
    try {
      await api.post("/admin/lawyers/verify", { lawyer_id: id, verified });
      toast.success(verified ? "Lawyer verified" : "Verification revoked");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  if (!lawyers.length) {
    return (
      <div className="card-flat p-12 text-center text-muted-foreground" data-testid="admin-lawyers-empty">
        No lawyers have signed up yet. Share the <code>/lawyer/register</code> page with vetted counsels.
      </div>
    );
  }
  return (
    <div className="card-flat overflow-x-auto" data-testid="admin-lawyers-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b hairline text-muted-foreground uppercase text-[10px] tracking-wider">
            <th className="text-left px-4 py-3">Counsel</th>
            <th className="text-left px-4 py-3">Bar ID</th>
            <th className="text-left px-4 py-3">Specialization</th>
            <th className="text-right px-4 py-3">Rate</th>
            <th className="text-right px-4 py-3">Endorsements</th>
            <th className="text-right px-4 py-3">Earnings</th>
            <th className="text-center px-4 py-3">Status</th>
            <th className="text-right px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {lawyers.map((lw) => (
            <tr key={lw.id} className="border-b hairline last:border-0" data-testid={`admin-lawyer-${lw.id.slice(0, 8)}`}>
              <td className="px-4 py-3">
                <div>{lw.name}</div>
                <div className="text-xs text-muted-foreground">{lw.email}</div>
              </td>
              <td className="px-4 py-3 text-xs">{lw.bar_council_id}</td>
              <td className="px-4 py-3 text-xs">{lw.specialization}</td>
              <td className="px-4 py-3 text-right num-metric">{inr(lw.rate_inr)}</td>
              <td className="px-4 py-3 text-right">{lw.endorsement_count}</td>
              <td className="px-4 py-3 text-right num-metric">{inr(lw.earnings_inr)}</td>
              <td className="px-4 py-3 text-center">
                {lw.verified ? (
                  <span className="text-[10px] px-2 py-1 bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] uppercase tracking-wider">Verified</span>
                ) : (
                  <span className="text-[10px] px-2 py-1 bg-yellow-500/10 text-yellow-500 uppercase tracking-wider">Pending</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {lw.verified ? (
                  <button onClick={() => verify(lw.id, false)} className="btn-ghost px-3 py-1.5 text-xs" data-testid={`admin-lawyer-revoke-${lw.id.slice(0, 8)}`}>
                    Revoke
                  </button>
                ) : (
                  <button onClick={() => verify(lw.id, true)} className="btn-primary px-3 py-1.5 text-xs" data-testid={`admin-lawyer-verify-${lw.id.slice(0, 8)}`}>
                    Verify
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  if (loading) return <div className="card-flat p-10 text-muted-foreground" data-testid="analytics-loading">Loading analytics…</div>;
  if (!data) return null;

  return (
    <div className="space-y-8" data-testid="admin-analytics-panel">
      <div className="flex items-center gap-3">
        <span className="eyebrow text-xs">Window</span>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1 text-xs border hairline ${days === d ? "bg-[hsl(var(--primary))] text-primary-foreground" : "bg-transparent"}`}
              data-testid={`analytics-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        <MiniStat label={`Pageviews · ${days}d`} value={data.total_pageviews} />
        <MiniStat label="Pageviews · 7d" value={data.last_7d_pageviews} />
        <MiniStat label="Pageviews · 24h" value={data.last_24h_pageviews} />
        <MiniStat label="Unique sessions · 24h" value={data.unique_sessions_24h} />
      </div>

      <section className="card-flat p-6">
        <div className="eyebrow mb-4">Most-used features (last {days} days)</div>
        {data.top_features?.length ? (
          <table className="w-full text-sm" data-testid="analytics-top-features">
            <thead>
              <tr className="border-b hairline text-muted-foreground uppercase text-[10px] tracking-wider">
                <th className="text-left py-2">Feature</th>
                <th className="text-right py-2">Views</th>
                <th className="text-right py-2">Avg time</th>
                <th className="text-right py-2">Sessions</th>
              </tr>
            </thead>
            <tbody>
              {data.top_features.map((r) => (
                <tr key={r.route} className="border-b hairline last:border-0" data-testid={`analytics-top-${r.route.replace(/\//g, "-")}`}>
                  <td className="py-2.5"><code className="text-xs">{r.route}</code></td>
                  <td className="py-2.5 text-right num-metric">{r.views}</td>
                  <td className="py-2.5 text-right">{r.avg_time_seconds}s</td>
                  <td className="py-2.5 text-right">{r.unique_sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-muted-foreground">No feature data yet.</div>
        )}
      </section>

      {data.least_used_features?.length > 0 && (
        <section className="card-flat p-6">
          <div className="eyebrow mb-4">Least-used features — candidates to promote or remove</div>
          <div className="space-y-2" data-testid="analytics-least-features">
            {data.least_used_features.slice(0, 5).map((r) => (
              <div key={r.route} className="flex items-center justify-between text-sm">
                <code className="text-xs">{r.route}</code>
                <span className="text-muted-foreground">{r.views} views · {r.avg_time_seconds}s avg</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {data.daily_series?.length > 0 && (
        <section className="card-flat p-6">
          <div className="eyebrow mb-4">Daily pageviews · last 14 days</div>
          <div className="flex items-end gap-2 h-32" data-testid="analytics-daily-bars">
            {data.daily_series.map((d) => {
              const maxV = Math.max(...data.daily_series.map((x) => x.views), 1);
              const h = (d.views / maxV) * 100;
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center" title={`${d.day}: ${d.views}`}>
                  <div className="w-full bg-[hsl(var(--secondary))]" style={{ height: `${h}%` }} />
                  <div className="text-[9px] text-muted-foreground mt-1 rotate-[-30deg] origin-top-left whitespace-nowrap">{d.day.slice(5)}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="card-flat p-6">
        <div className="eyebrow mb-4">All routes (top {data.top_routes.length})</div>
        <table className="w-full text-sm" data-testid="analytics-all-routes">
          <thead>
            <tr className="border-b hairline text-muted-foreground uppercase text-[10px] tracking-wider">
              <th className="text-left py-2">Route</th>
              <th className="text-right py-2">Views</th>
              <th className="text-right py-2">Avg time</th>
              <th className="text-right py-2">Sessions</th>
            </tr>
          </thead>
          <tbody>
            {data.top_routes.map((r) => (
              <tr key={r.route} className="border-b hairline last:border-0">
                <td className="py-2"><code className="text-xs">{r.route}</code></td>
                <td className="py-2 text-right num-metric">{r.views}</td>
                <td className="py-2 text-right">{r.avg_time_seconds}s</td>
                <td className="py-2 text-right">{r.unique_sessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="card-flat p-5">
      <div className="text-xs text-muted-foreground mb-2">{label}</div>
      <div className="num-metric text-3xl">{Number(value || 0).toLocaleString("en-IN")}</div>
    </div>
  );
}


function PortfolioAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [topN, setTopN] = useState(20);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/portfolio-analytics?top=${topN}&reveal=${reveal ? 1 : 0}`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load portfolio analytics"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reveal, topN]);

  const toggleReveal = () => {
    if (!reveal) {
      const ok = window.confirm("Reveal raw user PII (names, emails, phones)? This will be logged in the admin audit trail.");
      if (!ok) return;
    }
    setReveal(!reveal);
  };

  const exportCsv = async () => {
    if (reveal) {
      const ok = window.confirm("Export RAW (un-masked) portfolio analytics as CSV? This will be logged in the admin audit trail.");
      if (!ok) return;
    }
    try {
      const res = await api.get(`/admin/portfolio-analytics/export.csv?reveal=${reveal ? 1 : 0}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `estima-portfolio-analytics.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(reveal ? "Raw CSV downloaded · audit logged" : "Masked CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading) {
    return <div className="card-flat p-10 text-muted-foreground" data-testid="portfolio-analytics-loading">Loading portfolio analytics…</div>;
  }
  if (!data) return null;

  return <PortfolioAnalyticsView data={data} reveal={reveal} topN={topN} setTopN={setTopN} onToggleReveal={toggleReveal} onExportCsv={exportCsv} />;
}

function PortfolioAnalyticsView({ data, reveal, topN, setTopN, onToggleReveal, onExportCsv }) {
  const COLORS = ["hsl(var(--secondary))", "hsl(var(--primary))", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4"];
  const k = data.kpis || {};
  return (
    <div className="space-y-8" data-testid="portfolio-analytics-panel">
      {/* Header + controls */}
      <div className="card-flat p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Portfolio analytics</div>
            <h3 className="font-serif text-2xl">Aggregate user portfolio data — PII masked by default.</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Cross-user property aggregates for analysis. Use Reveal sparingly — every reveal/export is audit-logged.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              className="input-dark px-3 py-2 text-sm"
              value={topN}
              onChange={(e) => setTopN(Number(e.target.value))}
              data-testid="portfolio-analytics-top-select"
            >
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>Top {n} users</option>)}
            </select>
            <button
              onClick={onToggleReveal}
              className={`text-xs px-3 py-2 border ${reveal ? "border-destructive text-destructive" : "border-[hsl(var(--secondary))] text-[hsl(var(--secondary))]"}`}
              data-testid="portfolio-analytics-reveal-toggle"
            >
              {reveal ? "🔓 Hide PII" : "Reveal PII (audited)"}
            </button>
            <button
              onClick={onExportCsv}
              className="text-xs px-3 py-2 border hairline hover:border-[hsl(var(--secondary))] hover:text-[hsl(var(--secondary))] transition"
              data-testid="portfolio-analytics-export-csv"
            >
              ⬇ Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
        <MiniStat label="Users with properties" value={k.total_users_with_props} />
        <MiniStat label="Total properties" value={k.total_properties} />
        <div className="card-flat p-5">
          <div className="text-xs text-muted-foreground mb-2">Total portfolio value</div>
          <div className="num-metric text-2xl">{inrFull(k.total_portfolio_value || 0)}</div>
        </div>
        <MiniStat label="Avg properties / user" value={k.avg_props_per_user} />
        <div className="card-flat p-5">
          <div className="text-xs text-muted-foreground mb-2">Avg portfolio / user</div>
          <div className="num-metric text-2xl">{inrFull(k.avg_portfolio_value || 0)}</div>
        </div>
      </div>

      {/* Charts row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        <PortfolioChart title="Properties by status" data={data.by_status} colors={COLORS} testid="portfolio-chart-status" />
        <PortfolioChart title="Properties by type" data={data.by_type} colors={COLORS} testid="portfolio-chart-type" />
      </div>

      {/* Charts row 2 */}
      <div className="grid lg:grid-cols-2 gap-6">
        <PortfolioBar title="Top cities" data={data.by_city} testid="portfolio-chart-city" />
        <PortfolioBar title="Price distribution" data={data.by_price_bucket} testid="portfolio-chart-bucket" />
      </div>

      {/* Top users table */}
      <section className="card-flat p-0">
        <div className="p-5 border-b hairline flex items-center justify-between gap-4 flex-wrap">
          <div className="eyebrow">Top users by portfolio value (top {topN})</div>
          <div className="text-xs text-muted-foreground">
            {reveal ? "Showing raw PII — audited" : "PII masked"}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="portfolio-top-users-table">
            <thead>
              <tr className="border-b hairline text-muted-foreground uppercase text-[10px] tracking-wider">
                <th className="text-left p-3">#</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Phone</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-right p-3">Props</th>
                <th className="text-right p-3">Owned</th>
                <th className="text-right p-3">Sold</th>
                <th className="text-right p-3">Rented</th>
                <th className="text-right p-3">Monthly rent</th>
                <th className="text-right p-3">Portfolio value</th>
              </tr>
            </thead>
            <tbody>
              {data.top_users.map((r, i) => (
                <tr key={r.user_id || i} className="border-b hairline last:border-0" data-testid={`portfolio-user-row-${i}`}>
                  <td className="p-3 text-muted-foreground">{i + 1}</td>
                  <td className="p-3 truncate max-w-[180px]">{r.name || "—"}</td>
                  <td className="p-3 truncate max-w-[220px]">{r.email || "—"}</td>
                  <td className="p-3 truncate max-w-[160px]">{r.phone || "—"}</td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-wider ${r.plan === "pro" ? "text-[hsl(var(--primary))]" : "text-muted-foreground"}`}>
                      {r.plan}
                    </span>
                  </td>
                  <td className="p-3 text-right num-metric">{r.properties_count}</td>
                  <td className="p-3 text-right">{r.owned_count}</td>
                  <td className="p-3 text-right">{r.sold_count}</td>
                  <td className="p-3 text-right">{r.rented_count}</td>
                  <td className="p-3 text-right num-metric">{r.monthly_rent_income > 0 ? inr(r.monthly_rent_income) : "—"}</td>
                  <td className="p-3 text-right num-metric">{inrFull(r.portfolio_value || 0)}</td>
                </tr>
              ))}
              {data.top_users.length === 0 && (
                <tr><td colSpan={11} className="p-12 text-center text-muted-foreground">No users with properties yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function PortfolioChart({ title, data, colors, testid }) {
  if (!data || data.length === 0) {
    return (
      <div className="card-flat p-6" data-testid={testid}>
        <div className="eyebrow mb-4">{title}</div>
        <div className="text-muted-foreground text-sm">No data.</div>
      </div>
    );
  }
  return (
    <div className="card-flat p-6" data-testid={testid}>
      <div className="eyebrow mb-4">{title}</div>
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.count}`}>
            {data.map((slice) => <Cell key={slice.name} fill={colors[data.indexOf(slice) % colors.length]} />)}
          </Pie>
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function PortfolioBar({ title, data, testid }) {
  if (!data || data.length === 0) {
    return (
      <div className="card-flat p-6" data-testid={testid}>
        <div className="eyebrow mb-4">{title}</div>
        <div className="text-muted-foreground text-sm">No data.</div>
      </div>
    );
  }
  return (
    <div className="card-flat p-6" data-testid={testid}>
      <div className="eyebrow mb-4">{title}</div>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} interval={0} angle={-15} textAnchor="end" height={50} />
          <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
          <Bar dataKey="count" fill="hsl(var(--secondary))" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}



function EmailTestPanel() {
  const [config, setConfig] = useState(null);
  const [templateId, setTemplateId] = useState("welcome");
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/email-templates");
        setConfig(data);
      } catch (e) {
        toast.error("Couldn't load template list");
      }
    })();
  }, []);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!to) {
      toast.error("Enter a recipient");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/test-email", { template_id: templateId, to });
      const row = {
        ts: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        template: templateId,
        to,
        ok: data.ok,
        status: data.status,
        subject: data.subject,
        resend_id: data.resend_id,
      };
      setHistory((prev) => [row, ...prev].slice(0, 20));
      if (data.ok) toast.success(`Sent · ${data.subject}`);
      else toast.error(`Send failed (HTTP ${data.status})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-email-test-panel">
      <div className="card-flat p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="eyebrow mb-1">Email harness</div>
            <h3 className="font-serif text-2xl">Send any template to any address</h3>
            <p className="text-sm text-muted-foreground mt-1">Useful for spot-checking copy, deliverability, and DKIM after DNS changes.</p>
          </div>
          {config && (
            <div className="text-xs text-muted-foreground text-right shrink-0">
              <div>From: <span className="text-foreground">{config.from}</span></div>
              <div className={config.key_configured ? "text-[hsl(var(--secondary))]" : "text-destructive"}>
                {config.key_configured ? "✓ API key configured" : "✗ RESEND_API_KEY missing"}
              </div>
            </div>
          )}
        </div>
        <form onSubmit={send} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
          <select
            className="input-dark px-3 py-2.5 text-sm"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            data-testid="email-test-template"
          >
            {(config?.templates || []).map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <input
            type="email"
            className="input-dark px-3 py-2.5 text-sm"
            placeholder="recipient@example.com"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="email-test-to"
          />
          <button
            type="submit"
            disabled={sending || !to}
            className="btn-primary px-5 py-2.5 text-sm"
            data-testid="email-test-send"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </form>
      </div>

      {history.length > 0 && (
        <div className="card-flat p-6">
          <div className="eyebrow mb-3">Recent sends ({history.length})</div>
          <table className="w-full text-sm" data-testid="email-test-history">
            <thead>
              <tr className="border-b hairline text-muted-foreground uppercase text-[10px] tracking-wider">
                <th className="text-left py-2">Time</th>
                <th className="text-left py-2">Template</th>
                <th className="text-left py-2">Recipient</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Subject</th>
              </tr>
            </thead>
            <tbody>
              {history.map((r) => (
                <tr key={`${r.ts}-${r.resend_id || r.to}`} className="border-b hairline last:border-0">
                  <td className="py-2 text-xs text-muted-foreground">{r.ts}</td>
                  <td className="py-2"><code className="text-xs">{r.template}</code></td>
                  <td className="py-2 text-xs">{r.to}</td>
                  <td className={`py-2 text-xs ${r.ok ? "text-[hsl(var(--secondary))]" : "text-destructive"}`}>
                    {r.ok ? `✓ ${r.status}` : `✗ ${r.status || "err"}`}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground truncate max-w-[280px]">{r.subject}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-3">
            See full delivery + opens at <a className="underline" href="https://resend.com/emails" target="_blank" rel="noreferrer">resend.com/emails</a>.
          </p>
        </div>
      )}
    </div>
  );
}


function DataViewerPanel() {
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState("users");
  const [reveal, setReveal] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState([]);

  const loadCollections = async () => {
    try {
      const { data } = await api.get("/admin/data/collections");
      setCollections(data);
    } catch (e) { /* silent */ }
  };
  const loadRows = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/data/${selected}?limit=50&reveal=${reveal ? 1 : 0}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  const loadAudit = async () => {
    try {
      const { data } = await api.get("/admin/data/audit?limit=20");
      setAudit(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { loadCollections(); loadAudit(); }, []);
  useEffect(() => { loadRows(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [selected, reveal]);

  const toggleReveal = async () => {
    if (!reveal) {
      const ok = window.confirm(`Reveal raw PII for collection "${selected}"? This will be logged in the admin audit trail.`);
      if (!ok) return;
    }
    setReveal(!reveal);
  };

  const exportCsv = async () => {
    if (reveal) {
      const ok = window.confirm(`Export RAW (un-masked) ${selected} as CSV? This will be logged in the admin audit trail.`);
      if (!ok) return;
    }
    try {
      const res = await api.get(`/admin/data/${selected}/export.csv?reveal=${reveal ? 1 : 0}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const nameMatch = cd.match(/filename="([^"]+)"/);
      a.download = nameMatch ? nameMatch[1] : `estima-${selected}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(reveal ? "Raw CSV downloaded · audit logged" : "Masked CSV downloaded");
      if (reveal) loadAudit();
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const cols = rows.length ? Object.keys(rows[0]).slice(0, 8) : [];

  return (
    <div className="space-y-6" data-testid="admin-data-viewer">
      <div className="card-flat p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Data viewer</div>
            <h3 className="font-serif text-2xl">Inspect raw collections — PII masked by default.</h3>
          </div>
          <div className="flex items-center gap-3">
            <select
              className="input-dark px-3 py-2 text-sm"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              data-testid="data-viewer-select"
            >
              {collections.map((c) => (
                <option key={c.name} value={c.name}>{`${c.name} (${c.count})`}</option>
              ))}
            </select>
            <button
              onClick={toggleReveal}
              className={`text-xs px-3 py-2 border ${reveal ? "border-destructive text-destructive" : "border-[hsl(var(--secondary))] text-[hsl(var(--secondary))]"}`}
              data-testid="data-viewer-reveal-toggle"
            >
              {reveal ? "🔓 Hide PII" : "Reveal PII (audited)"}
            </button>
            <button
              onClick={exportCsv}
              className="text-xs px-3 py-2 border hairline hover:border-[hsl(var(--secondary))] hover:text-[hsl(var(--secondary))] transition"
              data-testid="data-viewer-export-csv"
            >
              ⬇ Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="card-flat p-0">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="data-viewer-empty">No rows.</div>
        ) : (
          <table className="w-full text-xs" data-testid="data-viewer-table">
            <thead>
              <tr className="border-b hairline text-muted-foreground uppercase text-[10px] tracking-wider">
                {cols.map((c) => <th key={c} className="text-left p-3">{c}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${selected}-${i}`} className="border-b hairline last:border-0" data-testid={`data-row-${i}`}>
                  {cols.map((c) => (
                    <td key={c} className="p-3 truncate max-w-[200px]">
                      {typeof r[c] === "object" && r[c] !== null ? <code className="text-[10px] text-muted-foreground">{JSON.stringify(r[c]).slice(0, 60)}…</code> : String(r[c] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {audit.length > 0 && (
        <div className="card-flat p-6">
          <div className="eyebrow mb-3">Recent reveal audit ({audit.length})</div>
          <div className="space-y-1.5 text-xs">
            {audit.slice(0, 10).map((a, i) => (
              <div key={`${a.at}-${i}`} className="flex items-center justify-between gap-4 text-muted-foreground">
                <span className="text-foreground">{a.actor_email}</span>
                <span>revealed {a.collection} ({a.count} rows)</span>
                <span>{new Date(a.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
